import React, { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useTheme } from "../../../contexts/ThemeContext";
import { getPerfil, changePassword } from "../../../services/wordpress/micuenta";

const formatearDireccion = (direccion) => {
  if (!direccion) return "";
  const { direccion_1, direccion_2, ciudad, provincia, cp, pais } = direccion;
  return [direccion_1, direccion_2, [cp, ciudad].filter(Boolean).join(" "), provincia, pais]
    .filter(Boolean)
    .join(", ");
};

export const DetallesCuenta = () => {
  const auth = useAuth();
  const { colors, mode } = useTheme() || { colors: {}, mode: "light" };
  const isDark = mode === "dark";

  const textPrimary = isDark ? "#f1f5f9" : "#1e293b";
  const textSecondary = isDark ? "#94a3b8" : "#64748b";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0";
  const accent = "#ECB236";
  const inputBg = isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9";

  const [formData, setFormData] = useState({
    nombre: auth.user?.fullname || "",
    usuario: auth.user?.username || "",
    email: auth.user?.email || "",
  });
  const [perfilError, setPerfilError] = useState(null);
  const [direcciones, setDirecciones] = useState({ facturacion: null, envio: null });

  useEffect(() => {
    if (!auth.apiToken || !auth.wpUrl) {
      setPerfilError("no-wp");
      return;
    }
    let cancelado = false;
    getPerfil({ wpUrl: auth.wpUrl, apiToken: auth.apiToken })
      .then((perfil) => {
        if (cancelado) return;
        setFormData((prev) => ({
          ...prev,
          nombre: [perfil.nombre, perfil.apellidos].filter(Boolean).join(" ") || prev.nombre,
          email: perfil.email || prev.email,
        }));
        setDirecciones({
          facturacion: formatearDireccion(perfil.direccion_facturacion),
          envio: formatearDireccion(perfil.direccion_envio),
        });
      })
      .catch(() => {
        if (!cancelado) setPerfilError("No se pudieron cargar los datos de WordPress.");
      });
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.apiToken, auth.wpUrl]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const [passwordForm, setPasswordForm] = useState({
    actual: "",
    nueva: "",
    confirmar: "",
  });
  const [passwordEstado, setPasswordEstado] = useState({
    enviando: false,
    error: null,
    ok: false,
  });

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
    setPasswordEstado({ enviando: false, error: null, ok: false });
  };

  const handlePasswordSubmit = async () => {
    if (!auth.apiToken || !auth.wpUrl) {
      setPasswordEstado({ enviando: false, error: "No hay conexión con WordPress.", ok: false });
      return;
    }
    if (passwordForm.nueva.length < 8) {
      setPasswordEstado({
        enviando: false,
        error: "La contraseña nueva debe tener al menos 8 caracteres.",
        ok: false,
      });
      return;
    }
    if (passwordForm.nueva !== passwordForm.confirmar) {
      setPasswordEstado({ enviando: false, error: "Las contraseñas nuevas no coinciden.", ok: false });
      return;
    }

    setPasswordEstado({ enviando: true, error: null, ok: false });
    try {
      await changePassword({
        wpUrl: auth.wpUrl,
        apiToken: auth.apiToken,
        passwordActual: passwordForm.actual,
        passwordNueva: passwordForm.nueva,
      });
      setPasswordForm({ actual: "", nueva: "", confirmar: "" });
      setPasswordEstado({ enviando: false, error: null, ok: true });
    } catch (e) {
      const mensaje = e?.response?.data?.message || "No se pudo cambiar la contraseña.";
      setPasswordEstado({ enviando: false, error: mensaje, ok: false });
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 10,
    border: `1px solid ${borderColor}`,
    backgroundColor: inputBg,
    color: textPrimary,
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: textSecondary,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  };

  const subheaderStyle = {
    margin: "0 0 16px",
    paddingBottom: 10,
    borderBottom: `1px solid ${borderColor}`,
    fontSize: 12,
    fontWeight: 700,
    color: textSecondary,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
      <div>
        {perfilError && perfilError !== "no-wp" && (
          <p style={{ fontSize: 13, color: "#e11d48", margin: "0 0 16px" }}>{perfilError}</p>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelStyle}>Nombre</label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              style={inputStyle}
              className="!cursor-not-allowed"
              disabled
            />
          </div>
          <div>
            <label style={labelStyle}>Usuario</label>
            <input
              type="text"
              name="usuario"
              value={formData.usuario}
              onChange={handleChange}
              style={inputStyle}
              className="!cursor-not-allowed"
              disabled
            />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              style={inputStyle}
              className="!cursor-not-allowed"
              disabled
            />
          </div>
        </div>
      </div>

      <div>
        <p style={subheaderStyle}>Direcciones</p>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 260px", minWidth: 0 }}>
            <label style={labelStyle}>Facturación</label>
            <p style={{ fontSize: 13, color: direcciones.facturacion ? textPrimary : textSecondary, fontStyle: direcciones.facturacion ? "normal" : "italic", margin: 0 }}>
              {direcciones.facturacion || "Aún no has configurado este tipo de dirección."}
            </p>
          </div>
          <div style={{ flex: "1 1 260px", minWidth: 0 }}>
            <label style={labelStyle}>Envío</label>
            <p style={{ fontSize: 13, color: direcciones.envio ? textPrimary : textSecondary, fontStyle: direcciones.envio ? "normal" : "italic", margin: 0 }}>
              {direcciones.envio || "Aún no has configurado este tipo de dirección."}
            </p>
          </div>
        </div>
      </div>

      <div>
        <p style={subheaderStyle}>Cambiar contraseña</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelStyle}>Contraseña actual</label>
            <input
              type="password"
              name="actual"
              value={passwordForm.actual}
              onChange={handlePasswordChange}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Nueva contraseña</label>
            <input
              type="password"
              name="nueva"
              value={passwordForm.nueva}
              onChange={handlePasswordChange}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Confirmar nueva contraseña</label>
            <input
              type="password"
              name="confirmar"
              value={passwordForm.confirmar}
              onChange={handlePasswordChange}
              style={inputStyle}
            />
          </div>
          {passwordEstado.error && (
            <p style={{ fontSize: 13, color: "#e11d48", margin: 0 }}>{passwordEstado.error}</p>
          )}
          {passwordEstado.ok && (
            <p style={{ fontSize: 13, color: "#16a34a", margin: 0 }}>Contraseña actualizada.</p>
          )}
          <button
            type="button"
            onClick={handlePasswordSubmit}
            disabled={passwordEstado.enviando}
            style={{
              alignSelf: "flex-start",
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              backgroundColor: accent,
              border: "none",
              borderRadius: 20,
              padding: "8px 20px",
              cursor: passwordEstado.enviando ? "default" : "pointer",
              opacity: passwordEstado.enviando ? 0.7 : 1,
            }}
          >
            {passwordEstado.enviando ? "Cambiando..." : "Cambiar contraseña"}
          </button>
        </div>
      </div>
    </div>
  );
};
